import { Timestamp } from '@bufbuild/protobuf';
import {
  CustomUploadResponse,
  DragAndDropFiles,
  FilePreviewCard,
  IStyles,
  InfoBar,
  InstructionsPanel,
  Loader,
  NoContentCard,
  PrimaryButton,
  deserify,
  formatDateAsDayMonth,
  formatHtmlLinesAsBulletList,
  getFileExtensionType,
  getFileSizeInMB,
  getFileType,
  getImageType,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  uploadFile,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import { FileEnum } from '@protos/content_management/content.db_pb';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { AssessmentStudentResponse } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { FileUploadPurpose } from '@protos/learning_management/lms.common.apis_pb';
import {
  AssessmentSessionModeEnum,
  SessionModeEnum,
  SubmissionType,
  TaskEnum,
  TaskStudentAttemptStatusEnum,
} from '@protos/learning_management/lms.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { ChangeEvent, DragEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../../app/Context/GlobalContextProvider';
import { useAppDispatch, useAppSelector } from '../../reduxStore/reduxHooks';
import { HOME } from '../../routeHandling/RoutesNomenclature';
import { onResourceClick } from '../../utils/learn';
import { setToastInfo } from '../Home/reducer/homeDashboard.slice';
import { setResourceRedirectionPath } from '../Learn/reducer/learn.slice';
import AssignedResources from './components/AssignedResources';
import { getLearningHwErrorMessage } from './utils';

// Duplicated and merged from Homework/HwStartCover, ActiveHw/component/CustomHomework
// and CompletedHw/component/CustomHomeworkDisplay (demo only).
const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    p: {
      xs: `${pxToRem(20)} ${pxToRem(18)} ${pxToRem(90)}`,
      md: pxToRem(20),
      lg: `${pxTovW(37)} ${pxTovW(240)}`,
    },
    gap: { xs: pxToRem(20), md: pxTovW(30) },
  },
  mainContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: { xs: pxToRem(20), md: pxTovW(20) },
  },
  leftPanel: {
    width: { xs: '100%', md: '45%', lg: pxTovW(620) },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxTovW(30) },
  },
  rightPanel: {
    width: { xs: '100%', md: '50%', lg: pxTovW(714) },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxTovW(30) },
  },
  buttonContainer: {
    position: { xs: 'fixed', md: 'unset' },
    bottom: { xs: pxToRem(10) },
    left: { xs: '5%' },
    width: { xs: '90%', md: '100%', lg: pxTovW(397) },
    zIndex: { xs: 100, md: 0 },
  },
};

export const LearningHomework = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedFunction } = useGlobalContext();
  const homeworkIdParam = new URLSearchParams(location.search).get(
    'assessmentId'
  );
  const homeworkId =
    homeworkIdParam && /^\d+$/.test(homeworkIdParam)
      ? BigInt(homeworkIdParam)
      : undefined;
  const studentId = deserify(
    useAppSelector((state) => state.auth.userInfo?.studentProfileId)
  );
  const homework_end_path = useAppSelector(
    (state) => state.homework.homework_end_path
  );
  const {
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler,
    LmsCommonAPIServiceV2ClientWithStatusCodeHandler,
    LmsHomewokStudentAPIServiceV2ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();

  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'loading'
  );
  const [assessment, setAssessment] = useState<AssessmentTask>();
  const [studentResponse, setStudentResponse] =
    useState<AssessmentStudentResponse>();
  const [sessionId, setSessionId] = useState<bigint>();
  const [userUploads, setUserUploads] = useState<CustomUploadResponse[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Server-resolved, in assignment order; resources deleted since assignment
  // are left out here and by the server's submit check.
  const assignedResources = studentResponse?.assignedResources ?? [];
  const completedResourceIds = studentResponse?.completedResourceIds ?? [];
  const allResourcesDone = assignedResources.every((r) =>
    completedResourceIds.includes(r.resourceId)
  );
  // Teacher chose "Resources only" (stored as SUBMISSION_OFFLINE): no file needed.
  const uploadRequired =
    assessment?.submissionType !== SubmissionType.SUBMISSION_OFFLINE;
  const hasUploads = userUploads.some(
    (item) =>
      item.fileUploadStatus === 'success' && !item.uploadFileData.isDeleted
  );
  const isSubmitted =
    studentResponse?.assessmentAttemptStatus ===
    TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_COMPLETED;
  // UX only: the server enforces the deadline.
  const isEnded =
    !!assessment?.assessmentTargetDate &&
    new Timestamp(assessment.assessmentTargetDate).toDate() < new Date();
  const isReadOnly = isSubmitted || isEnded;

  useEffect(() => {
    setSelectedFunction(() => () => navigate(homework_end_path || HOME));
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  // Runs on every mount, so returning from ResourceView refreshes completion.
  useEffect(() => {
    if (!homeworkId || !studentId) {
      setLoading('error');
      return;
    }
    (async () => {
      try {
        setLoading('loading');
        const [contentRes, responseRes] = await Promise.all([
          LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
            {
              personId: studentId,
              personType: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
              assessmentId: homeworkId,
            }
          ),
          LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.fetchStudentAssessmentResponse(
            { studentId, assessmentId: homeworkId }
          ),
        ]);
        const task = contentRes?.data?.assessment;
        if (!task) throw new Error('No homework returned');
        setAssessment(task);
        setStudentResponse(responseRes?.data);
        setLoading('completed');
      } catch (err) {
        console.error(err);
        setLoading('error');
      }
    })();
  }, [homeworkId, studentId]);

  const toastError = (err: unknown, fallback: string) => {
    console.error(err);
    dispatch(
      setToastInfo({
        label: getLearningHwErrorMessage(err, fallback),
        variant: 'error',
        open: true,
      })
    );
  };

  const openResource = (resourceId: string) => {
    if (!assessment) return;
    dispatch(
      setResourceRedirectionPath(`${location.pathname}${location.search}`)
    );
    onResourceClick(
      navigate,
      {
        resourceId,
        subjectId: assessment.subjectId,
        // Chapter id so the resource session's moduleId resolves to the chapter.
        chapterId: assessment.chapterId || assessment.moduleId,
      },
      SessionModeEnum.SESSION_MODE_RESOURCE
    );
  };

  // Submit needs a homework session; created once, on first upload or submit.
  const ensureSession = async () => {
    if (sessionId) return sessionId;
    const attemptStatus = studentResponse?.assessmentAttemptStatus;
    const sessionMode =
      attemptStatus ===
        TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_COMPLETED ||
      attemptStatus ===
        TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_TIMED_OUT
        ? AssessmentSessionModeEnum.ASSESSMENT_SESSION_MODE_REATTEMPT
        : attemptStatus ===
          TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_IN_PROGRESS
        ? AssessmentSessionModeEnum.ASSESSMENT_SESSION_MODE_RESUME
        : AssessmentSessionModeEnum.ASSESSMENT_SESSION_MODE_FIRST_ATTEMPT;
    const hwSession =
      await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.createStudentAssessmentSession(
        {
          studentId,
          assessmentId: homeworkId,
          sessionMode,
          sessionStatus:
            TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_IN_PROGRESS,
          clientTimestamp: Timestamp.fromDate(new Date()),
        }
      );
    if (!hwSession.sessionId) throw new Error('session id is missing');
    setSessionId(hwSession.sessionId);
    return hwSession.sessionId;
  };

  const handleUploadDoc = async (filesToUpload: FileList) => {
    if (!filesToUpload || filesToUpload.length === 0 || !assessment) return;
    const existingFiles = new Set(
      userUploads
        .filter((file) => !file.uploadFileData?.isDeleted)
        .map(
          (file) =>
            `${(file.uploadFileData as any).originalImagePath}-${file.fileSize}`
        )
    );
    const newUploads: CustomUploadResponse[] = [];
    const filesForUploads: File[] = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      if (existingFiles.has(`${file.name}-${file.size}`)) {
        dispatch(
          setToastInfo({
            label: `Duplicate file not allowed : ${file.name}`,
            variant: 'error',
            open: true,
          })
        );
        continue;
      }
      newUploads.push({
        uploadFileData: {
          signedUrl: '',
          isUploaded: false,
          originalImagePath: file.name,
          isDeleted: false,
          fileType: getFileType(file.type),
          fileExtensionType: getFileExtensionType(file.type),
          fileSizeInMb: getFileSizeInMB(file.size),
        },
        fileUploadStatus: 'pending',
        fileSize: file.size,
      });
      filesForUploads.push(file);
    }
    if (newUploads.length === 0) return;
    setUserUploads((prev) => [...prev, ...newUploads]);

    let hwSessionId: bigint | undefined;
    try {
      hwSessionId = await ensureSession();
    } catch (err) {
      toastError(err, 'Could not start the homework');
    }

    for (let i = 0; i < newUploads.length; i++) {
      const upload = newUploads[i];
      const file = filesForUploads[i];
      if (getFileType(file.type) === FileEnum.FILE_TYPE_UNKNOWN) {
        upload.fileUploadStatus = 'Invalid Type';
        continue;
      }
      //! max file size to be 50 MB
      if (getFileSizeInMB(file.size) > 50) {
        upload.fileUploadStatus = 'Size Exceeded';
        continue;
      }
      if (!hwSessionId) {
        upload.fileUploadStatus = 'Upload Error';
        continue;
      }
      try {
        const signedUrlResponse =
          await LmsCommonAPIServiceV2ClientWithStatusCodeHandler.getSignedUrlForFileUpload(
            {
              personId: studentId,
              homeworkId,
              hwSessionId: Number(hwSessionId),
              filesInfo: [
                {
                  originalFilePath: file.name,
                  mimeType: getImageType(file.type),
                  size: getFileSizeInMB(file.size),
                },
              ],
              personType: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
              fileUploadPurpose:
                FileUploadPurpose.FILE_UPLOAD_FOR_HW_SUBMISSION,
              sectionId: assessment.sectionId,
              subjectId: assessment.subjectId,
            }
          );
        const signed = signedUrlResponse?.response?.[0]?.signedUrl;
        const signedUrl = signed ? getMediaBasePath(signed) : undefined;
        const uploadResult = signedUrl
          ? await uploadFile({ file, signedUrl })
          : undefined;
        if (signedUrl && uploadResult?.success === true) {
          upload.uploadFileData.signedUrl = signedUrl;
          upload.uploadFileData.isUploaded = true;
          upload.fileUploadStatus = 'success';
        } else {
          upload.fileUploadStatus = 'Upload Error';
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        upload.fileUploadStatus = 'Upload Error';
      }
    }
    // The upload objects were mutated in place; publish a new array reference.
    setUserUploads((prev) => [...prev]);
  };

  const handleDeleteFunction = async (indexToDelete: number) => {
    const tempUserUploads = [...userUploads];
    tempUserUploads[indexToDelete].uploadFileData.isDeleted = true;
    setUserUploads(tempUserUploads);
  };

  const submitClickHandler = async () => {
    if (!homeworkId || !allResourcesDone || (uploadRequired && !hasUploads))
      return;
    try {
      setSubmitting(true);
      const hwSessionId = await ensureSession();
      const response =
        await LmsHomewokStudentAPIServiceV2ClientWithStatusCodeHandler.submitStudentHWResponse(
          {
            studentId,
            homeworkId,
            homeworkAttemptStatus:
              TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_COMPLETED,
            timestamp: Timestamp.fromDate(new Date()),
            sessionId: hwSessionId,
            userUploads: userUploads
              .filter((upload) => upload.fileUploadStatus === 'success')
              .map((upload) => upload.uploadFileData),
            taskType: TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM,
          }
        );
      if (response.status === 200) {
        dispatch(
          setToastInfo({
            label: uploadRequired ? 'Homework submitted' : 'Homework completed',
            variant: 'success',
            open: true,
          })
        );
        setUserUploads([]);
        setSessionId(undefined);
        const updated =
          await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.fetchStudentAssessmentResponse(
            { studentId, assessmentId: homeworkId }
          );
        setStudentResponse(updated?.data);
      }
    } catch (err) {
      toastError(err, 'Homework submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading === 'loading') return <Loader />;
  if (loading === 'error' || !assessment)
    return (
      <NoContentCard
        variant="error"
        icon="error"
        text="Could not load the homework. Please try again."
      />
    );

  const remarks = studentResponse?.teacherReview?.remarks?.join('') || '';
  const submittedUploads =
    studentResponse?.userUploads.filter((e) => e.isDeleted === false) ?? [];
  const maxScore = studentResponse?.metrics?.maxScore;

  return (
    <Box sx={styles.root}>
      <Typography variant="h1" fontWeight={'medium'}>
        {assessment.assessmentTitle || 'Learning Homework'}
      </Typography>
      <Box sx={styles.mainContainer}>
        <Box sx={styles.leftPanel}>
          <InfoBar
            contentList={[
              {
                iconName: 'calender',
                heading: formatDateAsDayMonth(assessment.assessmentTargetDate),
                subHeading: 'Deadline',
              },
              ...(isSubmitted && maxScore
                ? [
                    {
                      iconName: 'marks',
                      heading: `${studentResponse?.metrics?.responseScore}/${maxScore}`,
                      subHeading: 'Marks',
                    },
                  ]
                : assessment.maxMarks
                ? [
                    {
                      iconName: 'marks',
                      heading: assessment.maxMarks.toString(),
                      subHeading: 'Marks',
                    },
                  ]
                : []),
            ]}
          />
          <AssignedResources
            resources={assignedResources}
            completedResourceIds={completedResourceIds}
            onOpen={isReadOnly ? undefined : openResource}
          />
        </Box>

        <Box sx={styles.rightPanel}>
          <InstructionsPanel
            customSx={{ display: 'flex', flexGrow: 1, width: '100%' }}
            AttachmentList={assessment.taskUploads.filter(
              (e) => e.isDeleted === false
            )}
            Katexvalue={
              assessment.assessmentInstructions.join('\n') || '<p></p>'
            }
          />

          {isReadOnly ? (
            <Box>
              <Typography variant="h2" fontWeight={'medium'}>
                {uploadRequired ? 'Your Submission' : 'Status'}
              </Typography>
              {!uploadRequired ? (
                <Typography variant="elementBodyText">
                  {isSubmitted
                    ? 'Completed'
                    : 'Not completed before the deadline'}
                </Typography>
              ) : submittedUploads.length === 0 ? (
                <Typography variant="elementBodyText">
                  {isSubmitted
                    ? 'No uploads'
                    : 'Not submitted before the deadline'}
                </Typography>
              ) : (
                submittedUploads.map((elem, idx) => (
                  <Box key={idx} sx={{ mt: pxToRem(10) }}>
                    <FilePreviewCard
                      element={{
                        fileExtensionType: elem.fileExtensionType,
                        downloadUrl: getMediaBasePath(
                          elem.downloadUrl,
                          'schoolnetCdnImagesBucket'
                        ),
                        fileSizeInMb: elem.fileSizeInMb,
                        fileName: elem.fileName,
                        fileType: elem.fileType,
                        filePreviewUrl: getMediaBasePath(
                          elem.previewUrl,
                          'schoolnetCdnImagesBucket'
                        ),
                        gcpFileUrl: getMediaBasePath(
                          elem.gcpFileUrl,
                          'schoolnetCdnImagesBucket'
                        ),
                      }}
                    />
                  </Box>
                ))
              )}
              {remarks && (
                <InstructionsPanel
                  title="Remarks :"
                  customSx={{
                    display: 'flex',
                    flexGrow: 1,
                    mt: pxToRem(20),
                    maxHeight: { xs: pxToRem(200), lg: pxTovW(300) },
                  }}
                  Katexvalue={formatHtmlLinesAsBulletList(remarks)}
                />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: pxToRem(20), md: pxTovW(30) },
                alignItems: 'center',
              }}
            >
              {uploadRequired && (
                <DragAndDropFiles
                  handleDropFunction={(event: DragEvent<HTMLDivElement>) =>
                    handleUploadDoc(event.dataTransfer.files)
                  }
                  handleChangeFunction={async (
                    event: ChangeEvent<HTMLInputElement>
                  ) => {
                    await handleUploadDoc(event.target.files as FileList);
                    // Reset so selecting the same file again triggers onChange
                    if (event.target) event.target.value = '';
                  }}
                  handleDeleteFunction={handleDeleteFunction}
                  userUploads={userUploads}
                />
              )}
              {!allResourcesDone && (
                <Typography variant="bodyText" color="error.main">
                  {uploadRequired
                    ? 'Complete all assigned resources to submit.'
                    : 'Complete all assigned resources to finish this homework.'}
                </Typography>
              )}
              <Box sx={styles.buttonContainer}>
                <PrimaryButton
                  fullWidth
                  disabled={
                    submitting ||
                    !allResourcesDone ||
                    (uploadRequired && !hasUploads) ||
                    userUploads.some((u) => u.fileUploadStatus === 'pending')
                  }
                  onClick={submitClickHandler}
                >
                  <Typography
                    variant="h3"
                    color={'common.white'}
                    fontWeight={700}
                  >
                    {uploadRequired ? 'SUBMIT' : 'MARK AS COMPLETE'}
                  </Typography>
                </PrimaryButton>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
